/**
 * "Continue with Dreamtree" for store staff (DTID-44).
 *
 * The server says whether Dreamtree ID sign-in is on: `/api/meta/context`
 * carries `sso: { mode, startPath, logoutPath }` only when `AUTH_MODE` is
 * `hybrid`. In local mode the field is absent and the sign-in page is exactly
 * the password form it always was (no extra request either).
 *
 * The flow is full-page navigation, never fetch: the IdP sets its own cookies
 * and the callback sets ours. It comes back to `<surface>/login?sso=1`, where
 * the page reads the new session (`GET /api/auth/sso/session`) into the auth
 * store. The store (tenant) is pinned by the SERVER from the host, so the
 * start link carries no `dt_tenant` of its own.
 */

const SSO_MODES = new Set(['hybrid', 'sso']);
let config = null;

/** Called once at boot with the context's `sso` field (or nothing). */
export function setSsoConfig(value) {
  config = value && SSO_MODES.has(value.mode) ? value : null;
}

/** `{ mode, startPath, logoutPath }` when SSO is on, else null. */
export function getSsoConfig() {
  return config;
}

/** A same-origin relative path, or null (mirrors the server's safeReturnTo). */
function safePath(value) {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || /[\\\s]/.test(value)) return null;
  return value;
}

/**
 * The start link. `loginPath` is this surface's sign-in page as the browser
 * sees it (`/admin/login`, `/service/login`); `from` is where to go afterwards,
 * inside the surface.
 */
export function ssoStartUrl({ startPath = '/auth/sso/start', loginPath = '/admin/login', from = null } = {}) {
  const back = new URLSearchParams({ sso: '1' });
  const target = safePath(from);
  if (target) back.set('from', target);
  const login = safePath(loginPath) || '/admin/login';
  return `${startPath}?${new URLSearchParams({ return_to: `${login}?${back.toString()}` }).toString()}`;
}

const ERRORS = {
  not_a_member: 'Your Dreamtree ID has no staff account in this store. Ask the store admin to invite you, or sign in with your password.',
  user_inactive: 'This staff account is not active.',
  sso_authorization_error: 'Sign-in with Dreamtree was not completed.',
  sso_state_mismatch: 'Sign-in with Dreamtree could not be verified. Please try again.',
};

/** The sentence for a callback refusal code, never the code's own text. */
export function ssoErrorMessage(code) {
  if (!code) return '';
  return ERRORS[code] || 'Sign-in with Dreamtree could not be completed. Please try again.';
}

/** Read the session the callback just minted, in the shape `login` returns. */
export async function fetchSsoSession(fetchImpl = globalThis.fetch) {
  const res = await fetchImpl('/api/auth/sso/session', { credentials: 'include' });
  if (!res.ok) throw new Error('Sign-in with Dreamtree did not create a session. Please try again.');
  const body = await res.json();
  return (body && body.data) || body;
}

/**
 * Sign out of the store AND Dreamtree ID: a real same-origin form POST that
 * the server answers with a redirect to the IdP's end-session endpoint.
 */
export function signOutThroughDreamtree(logoutPath = (config && config.logoutPath) || '/auth/sso/logout', doc = globalThis.document) {
  const form = doc.createElement('form');
  form.method = 'POST';
  form.action = logoutPath;
  form.style.display = 'none';
  doc.body.appendChild(form);
  form.submit();
}
